import {
  applyOp,
  applyOps,
  type BoardState,
  cloneState,
  emptyState,
  invertOps,
  type Op,
  type ServerMessage,
  Y_SYNC,
} from '@shared/board'
import * as decoding from 'lib0/decoding'
import * as encoding from 'lib0/encoding'
import * as syncProtocol from 'y-protocols/sync'
import { type GalleryPreviewData } from '@shared/gallery'
import {
  type SessionCommand,
  type SessionEvent,
  type SessionMeta,
  type SessionSync,
} from '@shared/session'
import * as Y from 'yjs'
import { type Material } from '@shared/material'
import { dbg } from '@/lib/debug'

export type ConnStatus = 'connecting' | 'connected' | 'offline'

type Pending = { cid: string; ops: Op[] }
type UndoEntry = { forward: Op[]; inverse: Op[] }

export type Snapshot = {
  state: BoardState
  status: ConnStatus
  /** False until the first `init` arrives. */
  ready: boolean
  canUndo: boolean
  canRedo: boolean
}

export type Touched = Readonly<Record<string, number>>

const REMOTE = Symbol('remote')

const BOARD_MESSAGES = new Set(['init', 'ops', 'measured', 'reject', 'touched', 'materials'])

/**
 * Client half of a board. Holds the server-confirmed state plus this client's
 * pending ops layered on top (`view`), so local edits are instant and every
 * client still converges on the server's order.
 */
export class BoardClient {
  readonly doc = new Y.Doc()
  private confirmed: BoardState = emptyState()
  private pending: Pending[] = []
  private undoStack: UndoEntry[] = []
  private redoStack: UndoEntry[] = []
  private ws: WebSocket | null = null
  private retry = 0
  private retryTimer: ReturnType<typeof setTimeout> | undefined
  private measureQueue = new Map<string, { id: string; w: number; h: number }>()
  private measureTimer: ReturnType<typeof setTimeout> | undefined
  private listeners = new Set<() => void>()
  private snapshot: Snapshot
  private closed = false
  private nextCid = 0
  private rejectHandler: ((reason: string) => void) | null = null

  private readonly boardId: string
  /** An example from the gallery, shown offline and read only (no socket, no changes). */
  private readonly preview: GalleryPreviewData | null

  constructor(boardId: string, preview: GalleryPreviewData | null = null) {
    this.boardId = boardId
    this.preview = preview
    this.snapshot = {
      state: emptyState(),
      status: preview ? 'connected' : 'connecting',
      ready: !!preview,
      canUndo: false,
      canRedo: false,
    }
    this.doc.on('update', (update: Uint8Array, origin: unknown) => {
      dbg('ydoc update', origin === REMOTE ? 'remote' : 'local', update.byteLength)
      if (origin === REMOTE) return
      const enc = encoding.createEncoder()
      encoding.writeVarUint(enc, Y_SYNC)
      syncProtocol.writeUpdate(enc, update)
      this.sendRaw(encoding.toUint8Array(enc))
    })
    if (preview) this.fillPreview(preview)
    else this.connect()
  }

  /** True for a gallery preview: nothing can be changed, nothing is sent. */
  get readOnly(): boolean {
    return !!this.preview
  }

  private fillPreview(p: GalleryPreviewData) {
    const s = emptyState()
    for (const i of p.items) s.items.set(i.id, i)
    for (const e of p.edges) s.edges.set(e.id, e)
    this.confirmed = s
    if (p.ydoc) {
      const bin = atob(p.ydoc)
      const bytes = new Uint8Array(bin.length)
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
      Y.applyUpdate(this.doc, bytes, REMOTE)
    }
    this.snapshot = { ...this.snapshot, state: s }
  }

  /** Called when the server or a stale undo refuses a change. Returns an unsubscribe. */
  onReject(fn: (reason: string) => void) {
    this.rejectHandler = fn
    return () => {
      if (this.rejectHandler === fn) this.rejectHandler = null
    }
  }

  // ---------- store ----------

  subscribe = (fn: () => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  getSnapshot = () => this.snapshot

  /**
   * When things last changed, on the server's clock (ServerMessage 'touched').
   * Kept apart from the board snapshot so these frequent reports re-render
   * only what reads them.
   */
  private touched: Touched = {}
  private readonly touchedListeners = new Set<() => void>()

  subscribeTouched = (fn: () => void) => {
    this.touchedListeners.add(fn)
    return () => this.touchedListeners.delete(fn)
  }

  getTouched = (): Touched => this.touched

  /** The board's library (MATERIALS.md), as the server last sent it. */
  private materials: readonly Material[] = []
  private readonly materialListeners = new Set<() => void>()

  subscribeMaterials = (fn: () => void) => {
    this.materialListeners.add(fn)
    return () => this.materialListeners.delete(fn)
  }

  getMaterials = (): readonly Material[] => this.materials

  material(id: string): Material | undefined {
    return this.materials.find((m) => m.id === id)
  }

  /** A material just added by this page, known before the server's list arrives. */
  addKnownMaterial(m: Material) {
    if (this.material(m.id)) return
    this.materials = [...this.materials, m]
    for (const fn of this.materialListeners) fn()
  }

  get view(): BoardState {
    return this.snapshot.state
  }

  private emit(patch: Partial<Snapshot> = {}) {
    let state = this.confirmed
    if (this.pending.length) {
      state = cloneState(this.confirmed)
      for (const p of this.pending) {
        for (const op of p.ops) {
          try {
            applyOp(state, op, Date.now())
          } catch {
            // superseded by a concurrent change; the server will reject it too
          }
        }
      }
    }
    this.snapshot = {
      ...this.snapshot,
      ...patch,
      state,
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
    }
    for (const fn of this.listeners) fn()
  }

  // ---------- ops ----------

  /** Apply ops locally now and send them. `undoable: false` for things like live drags. */
  apply(ops: Op[], { undoable = true }: { undoable?: boolean } = {}) {
    if (!ops.length || this.preview) return
    const before = this.view
    try {
      applyOps(before, ops, Date.now())
    } catch (e) {
      this.rejectHandler?.(String(e))
      return
    }
    if (undoable) {
      this.undoStack.push({ forward: ops, inverse: invertOps(before, ops) })
      if (this.undoStack.length > 200) this.undoStack.shift()
      this.redoStack = []
    }
    this.submit(ops)
  }

  undo() {
    if (this.preview) return
    const entry = this.undoStack.pop()
    if (!entry) return
    if (!this.trySubmit(entry.inverse)) {
      this.undoStack = []
      this.rejectHandler?.('stale-undo')
    } else this.redoStack.push(entry)
    this.emit()
  }

  redo() {
    if (this.preview) return
    const entry = this.redoStack.pop()
    if (!entry) return
    if (!this.trySubmit(entry.forward)) {
      this.redoStack = []
      this.rejectHandler?.('stale-undo')
    } else this.undoStack.push(entry)
    this.emit()
  }

  private trySubmit(ops: Op[]): boolean {
    try {
      applyOps(this.view, ops, Date.now())
    } catch {
      return false
    }
    this.submit(ops)
    return true
  }

  private submit(ops: Op[]) {
    const cid = `${Date.now().toString(36)}-${this.nextCid++}`
    this.pending.push({ cid, ops })
    this.sendJson({ t: 'ops', cid, ops })
    this.emit()
  }

  measure(id: string, w: number, h: number) {
    const item = this.view.items.get(id)
    if (item && Math.abs(item.w - w) < 1 && Math.abs(item.h - h) < 1) return
    this.measureQueue.set(id, { id, w, h })
    // Reflect locally right away so layout-dependent UI (groups, edges) is accurate.
    const cur = this.confirmed.items.get(id)
    if (cur) {
      this.confirmed = cloneState(this.confirmed)
      this.confirmed.items.set(id, { ...cur, w, h })
      this.emit()
    }
    this.measureTimer ??= setTimeout(() => {
      this.measureTimer = undefined
      const sizes = [...this.measureQueue.values()]
      this.measureQueue.clear()
      if (sizes.length) this.sendJson({ t: 'measure', sizes })
    }, 250)
  }

  // ---------- connection ----------

  private connect() {
    if (this.closed) return
    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const ws = new WebSocket(`${proto}://${location.host}/api/boards/${this.boardId}/ws`)
    ws.binaryType = 'arraybuffer'
    this.ws = ws
    ws.onopen = () => {
      this.retry = 0
    }
    ws.onmessage = (ev: MessageEvent<string | ArrayBuffer>) => {
      if (typeof ev.data === 'string') {
        const msg = JSON.parse(ev.data) as ServerMessage | SessionEvent | SessionSync
        if (BOARD_MESSAGES.has(msg.t)) this.onServer(msg as ServerMessage)
        else for (const fn of this.sessionListeners) fn(msg as SessionEvent | SessionSync)
      } else this.onYjs(new Uint8Array(ev.data))
    }
    ws.onclose = () => {
      if (this.ws !== ws) return
      this.ws = null
      if (this.closed) return
      this.emit({ status: 'offline' })
      this.retryTimer = setTimeout(
        () => {
          this.connect()
        },
        Math.min(10_000, 400 * 2 ** this.retry++),
      )
    }
  }

  private onServer(msg: ServerMessage) {
    switch (msg.t) {
      case 'init': {
        const s = emptyState()
        for (const i of msg.items) s.items.set(i.id, i)
        for (const e of msg.edges) s.edges.set(e.id, e)
        this.confirmed = s
        // Re-send anything the server may not have seen before the reconnect.
        for (const p of this.pending) this.sendJson({ t: 'ops', cid: p.cid, ops: p.ops })
        for (const cmd of this.outbox.splice(0)) this.sendJson(cmd)
        this.emit({ status: 'connected', ready: true })
        return
      }
      case 'ops': {
        // Copy on write: the snapshot must change identity for React to notice.
        this.confirmed = cloneState(this.confirmed)
        for (const op of msg.ops) {
          try {
            applyOp(this.confirmed, op, Date.now())
          } catch {
            // already reflected (e.g. a replayed create after reconnect)
          }
        }
        if (msg.cid) this.pending = this.pending.filter((p) => p.cid !== msg.cid)
        this.emit()
        return
      }
      case 'measured': {
        this.confirmed = cloneState(this.confirmed)
        for (const { id, w, h } of msg.sizes) {
          const cur = this.confirmed.items.get(id)
          if (cur) this.confirmed.items.set(id, { ...cur, w, h })
        }
        this.emit()
        return
      }
      case 'touched': {
        // Its own store: these reports come often and only embeds read them.
        this.touched = { ...this.touched, ...msg.at }
        for (const fn of this.touchedListeners) fn()
        return
      }
      case 'materials': {
        this.materials = msg.materials
        for (const fn of this.materialListeners) fn()
        return
      }
      case 'reject': {
        this.pending = this.pending.filter((p) => p.cid !== msg.cid)
        this.rejectHandler?.(msg.reason)
        this.emit()
        return
      }
    }
  }

  private onYjs(data: Uint8Array) {
    const dec = decoding.createDecoder(data)
    if (decoding.readVarUint(dec) !== Y_SYNC) return
    const enc = encoding.createEncoder()
    encoding.writeVarUint(enc, Y_SYNC)
    syncProtocol.readSyncMessage(dec, enc, this.doc, REMOTE)
    if (encoding.length(enc) > 1) this.sendRaw(encoding.toUint8Array(enc))
    // After the server's step 1, also ask for its state.
    if (data[1] === syncProtocol.messageYjsSyncStep1) {
      const e2 = encoding.createEncoder()
      encoding.writeVarUint(e2, Y_SYNC)
      syncProtocol.writeSyncStep1(e2, this.doc)
      this.sendRaw(encoding.toUint8Array(e2))
    }
  }

  // ---------- sessions (AI) ----------

  private sessionListeners = new Set<(msg: SessionEvent | SessionSync) => void>()
  private outbox: SessionCommand[] = []

  /** Session state and events from the board object (see shared/session.ts). */
  onSessionMessage(fn: (msg: SessionEvent | SessionSync) => void) {
    this.sessionListeners.add(fn)
    // A preview answers from its own data: the conversations it has.
    if (this.preview) {
      const list = this.previewList()
      queueMicrotask(() => {
        fn(list)
      })
    }
    return () => {
      this.sessionListeners.delete(fn)
    }
  }

  /** Send a session command; held until the socket is (re)connected. */
  sendSession(cmd: SessionCommand) {
    if (this.preview) {
      this.answerPreview(cmd)
      return
    }
    if (this.ws?.readyState === WebSocket.OPEN && this.snapshot.ready) this.sendJson(cmd)
    else this.outbox.push(cmd)
  }

  private previewList(): SessionSync {
    const p = this.preview
    const sessions: SessionMeta[] = (p?.conversations ?? []).map((c) => ({
      id: c.id,
      title: c.title,
      titleSource: 'user',
      model: c.model,
      effort: c.effort,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      running: false,
      costMicros: 0,
    }))
    const fired: Record<string, string[]> = {}
    for (const c of p?.conversations ?? [])
      for (const e of c.entries)
        if (e.kind === 'turn_start')
          for (const m of e.trigger.fired ?? []) fired[m] = [...(fired[m] ?? []), c.id]
    return { t: 's.list', sessions, fired }
  }

  /** What the board object would answer, for the few things a read-only preview asks. */
  private answerPreview(cmd: SessionCommand) {
    const reply = (msg: SessionEvent | SessionSync) => {
      queueMicrotask(() => {
        for (const fn of this.sessionListeners) fn(msg)
      })
    }
    const conv =
      'sessionId' in cmd
        ? this.preview?.conversations.find((c) => c.id === cmd.sessionId)
        : undefined
    switch (cmd.t) {
      case 's.load':
        if (conv)
          reply({ t: 's.transcript', sessionId: conv.id, entries: conv.entries, queued: [] })
        return
      case 's.changes': {
        if (!conv) return
        const end = conv.entries.find((e) => e.turnId === cmd.turnId && e.kind === 'turn_end')
        // Like any conversation that came with a board: what it changed is not kept.
        reply({
          t: 's.changes',
          set: {
            sessionId: conv.id,
            turnId: cmd.turnId,
            unavailable: true,
            endedAt: end?.ts ?? null,
            changes: [],
            later: [],
          },
        })
        return
      }
      default:
        return
    }
  }

  private sendJson(msg: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg))
  }

  private sendRaw(data: Uint8Array) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(data as Uint8Array<ArrayBuffer>)
  }

  destroy() {
    this.closed = true
    clearTimeout(this.retryTimer)
    clearTimeout(this.measureTimer)
    this.ws?.close()
    this.doc.destroy()
  }
}
